import { useNavigate } from '@tanstack/react-router';
import { CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { MigrationSummary, MigrationStatus } from '@/types';

interface RecentMigrationsProps {
  migrations: MigrationSummary[];
  className?: string | undefined;
}

const statusConfig: Record<MigrationStatus, {
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  icon: typeof CheckCircle;
  color: string;
}> = {
  InProgress: {
    label: 'In Progress',
    variant: 'default',
    icon: Loader2,
    color: 'text-primary',
  },
  Succeeded: {
    label: 'Succeeded',
    variant: 'outline',
    icon: CheckCircle,
    color: 'text-green-600',
  },
  Partial: {
    label: 'Partial',
    variant: 'secondary',
    icon: AlertCircle,
    color: 'text-yellow-600',
  },
  Failed: {
    label: 'Failed',
    variant: 'destructive',
    icon: XCircle,
    color: 'text-destructive',
  },
};

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function RecentMigrations({ migrations, className }: RecentMigrationsProps) {
  const navigate = useNavigate();

  // Get the 5 most recent migrations
  const recentMigrations = [...migrations]
    .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
    .slice(0, 5);

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Recent Migrations</CardTitle>
        <CardDescription>Latest migration activity</CardDescription>
      </CardHeader>
      <CardContent>
        {recentMigrations.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            No migrations yet. Start your first migration!
          </p>
        ) : (
          <div className="space-y-4">
            {recentMigrations.map((migration) => {
              const status = statusConfig[migration.status];
              const StatusIcon = status.icon;

              return (
                <div
                  key={migration.id}
                  className="flex items-center gap-4 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() =>
                    navigate({
                      to: '/migrations/$migrationId',
                      params: { migrationId: migration.id },
                    })
                  }
                >
                  <StatusIcon
                    className={cn(
                      'h-5 w-5 flex-shrink-0',
                      status.color,
                      migration.status === 'InProgress' && 'animate-spin'
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{migration.organizationName}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{migration.startedByUserName}</span>
                      <span>•</span>
                      <span>{formatRelativeTime(migration.startedAt)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={status.variant}>{status.label}</Badge>
                    <span className="text-sm text-muted-foreground">
                      {migration.completedModules}/{migration.totalModules}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
